import { useCallback } from 'react';
import type { ExportReportType } from '../../domains/export/models/ExportRequest';
import type { ExportQualityOption } from './useExportJob';
import { useDiagramDocument } from '../../contexts/DiagramDocumentContext';
import { useManagedExport } from './useManagedExport';
import { useI18nLocale } from '../useTranslation';
import { usePerspectiveStore } from '../../stores/perspectiveStore';

interface UseReportExportOptions {
  diagramId: string | undefined;
}

interface StartReportExportParams {
  reportType: ExportReportType;
  reportVariant?: string;
  format?: 'docx' | 'pdf' | 'csv' | 'json';
  quality?: ExportQualityOption;
  progressSince?: string;
}

export function useReportExport({ diagramId }: UseReportExportOptions) {
  const { flushPendingUpdates } = useDiagramDocument();
  const locale = useI18nLocale();
  const perspective = usePerspectiveStore((state) => state.perspective);
  const healthFocusCondition = usePerspectiveStore((state) => state.healthFocusCondition);
  const healthFocusIndexPersonId = usePerspectiveStore((state) => state.healthFocusIndexPersonId);

  const {
    isOpen,
    open,
    close,
    onModalOpenChange,
    startExport,
    handleCancel,
    isSubmitting,
    isSaving,
    isDownloading,
    error,
    job,
  } = useManagedExport({
    diagramId,
    prepare: flushPendingUpdates,
  });

  const startReportExport = useCallback(
    async ({
      reportType,
      reportVariant,
      format = 'docx',
      quality = 'normal',
      progressSince,
    }: StartReportExportParams) => {
      if (!reportType) return;
      await startExport({
        format,
        quality,
        reportType,
        reportVariant,
        ...(progressSince ? { progressSince } : {}),
        locale,
        ...(perspective === 'general' || perspective === 'cultural' || perspective === 'health'
          ? { exportViewpoint: perspective }
          : {}),
        ...(perspective === 'health' && healthFocusCondition
          ? { healthFocusCondition, healthFocusIndexPersonId: healthFocusIndexPersonId ?? undefined }
          : {}),
      });
    },
    [healthFocusCondition, healthFocusIndexPersonId, locale, perspective, startExport],
  );

  return {
    isOpen,
    open,
    close,
    onModalOpenChange,
    isSubmitting,
    isSaving,
    isDownloading,
    error,
    job,
    startReportExport,
    handleCancel,
  };
}
